import { useContext, useEffect, useRef } from 'react';
import { useTableManipulation } from './useTableManipulation';
import { SidePanelProviderContext } from 'components/editor/SidePanel/content/SidePanelModelsProvider';
import type { GridApiPro } from '@mui/x-data-grid-pro';
import InputWithTooltips from './components/InputWithTooltips';

import { useTranslation } from 'react-i18next';
import { ActiveEditorContext } from '../../TiptapEditor/ActiveEditorContext';
import { useTableContentUndoRedo } from '../../../hooks/useTableContentUndoRedo';
import { useAppSelector } from '../../../grid/reduxStore/Store';
import type { RootState } from '../../../grid/reduxStore/Store';
import { selectContentTable } from '../../../grid/reduxStore/editorSlice';

type TooltipInputProps = {
  params: any;
  blockId: string;
  numberOfDecimalPlaces?: number;
  sectionId: string;
  currencySymbol?: string;
};

const CellInputs = ({ params, blockId, numberOfDecimalPlaces, sectionId, currencySymbol }: TooltipInputProps): React.ReactElement => {
  const { t } = useTranslation();
  const { getUpdatedRowsTableDataFromCellEdit, getTableData } = useTableManipulation();
  const { handleTableContentChanged } = useTableContentUndoRedo();
  const { setActiveEditor } = useContext(ActiveEditorContext);

  const { toggledTableSettingsPanel, setToggledTableSettingsPanel } = useContext(SidePanelProviderContext);

  const { id, field, row } = params;
  const isCellHidden = row?.cellConfig?.[field]?.hideCell;
  const tooltipTitle = isCellHidden && t('document.grid.table.cell_hidden_tooltip');

  const lastSentValueRef = useRef<string | null>(null);
  const reduxCellValue = useAppSelector((state: RootState) => {
    const tableData = selectContentTable(state, sectionId, blockId);
    if (!tableData?.rows) return undefined;
    const currentRow = tableData.rows.find((r) => r.id === id);
    return currentRow?.[field] as string | undefined;
  });

  const handleOnInputValueChange = async (value: string) => {
    const updatedTableData = getUpdatedRowsTableDataFromCellEdit(params, value, blockId, sectionId);
    const tableData = getTableData(blockId, sectionId);
    if (!updatedTableData || !tableData) return;

    lastSentValueRef.current = value;

    handleTableContentChanged(blockId, sectionId, tableData, updatedTableData);
  };

  const handleOnPressEnter = async (event: React.KeyboardEvent<HTMLInputElement>, apiRef: GridApiPro, localValue: string) => {
    if (event.key === 'Enter' && toggledTableSettingsPanel) {
      apiRef.stopCellEditMode({ id, field });
      const allRowIds = apiRef.getAllRowIds();
      const currentRowIndex = allRowIds.findIndex((rowId) => rowId === id);
      const nextRowId = allRowIds[currentRowIndex + 1];

      if (nextRowId) {
        apiRef.setEditCellValue({ id, field, value: localValue });
        apiRef.setRowSelectionModel([nextRowId]);
        setToggledTableSettingsPanel({
          tableApi: {
            selectedModel: apiRef.getCellParams(nextRowId, field),
            tableCallbackDetails: toggledTableSettingsPanel.tableApi.tableCallbackDetails,
          },
        });
        apiRef.startCellEditMode({
          id: nextRowId,
          field,
        });
      }
    }
  };

  useEffect(() => {
    setActiveEditor(null);
  }, []);

  return (
    <InputWithTooltips
      params={params}
      blockId={blockId || ''}
      numberOfDecimalPlaces={numberOfDecimalPlaces}
      sectionId={sectionId}
      tooltipTitle={tooltipTitle}
      onInputChange={handleOnInputValueChange}
      onPressEnter={handleOnPressEnter}
      currencySymbol={currencySymbol}
      reduxCellValue={reduxCellValue}
      lastSentValueRef={lastSentValueRef}
    />
  );
};

export default CellInputs;
